import { RiPulseLine } from 'react-icons/ri';
import { VscBeaker } from 'react-icons/vsc';
import { BiArrowToBottom, BiCalendarWeek, BiCalculator } from 'react-icons/bi';
import { TbBrandZapier } from 'react-icons/tb';
import { IoCalendarOutline as SidebarCalendarIcon } from 'react-icons/io5';
import { MdAccessTime } from 'react-icons/md';
import { AiOutlineFire } from 'react-icons/ai';
import { PiGavel } from 'react-icons/pi';
import { BsNewspaper } from 'react-icons/bs';
import { BiBitcoin, BiDollar } from 'react-icons/bi';
import { HiOutlineAcademicCap, HiOutlineBookOpen } from 'react-icons/hi';

import { FaUser } from 'react-icons/fa';
import { GiLouvrePyramid } from 'react-icons/gi';

import {
  ProductionAlertPrices,
  DevAlertPrices,
  ProductionExclusiveEventPrices,
  DevExclusiveEventPrices,
  CHECKOUT_COMPLETE_PAGE,
} from 'constants/payments';
// import { FiLogOut } from 'react-icons/fi';

export { CHECKOUT_COMPLETE_PAGE };
export const OFFER_CARD_API = 'payments/stripe/offer-card';
export const MEMBER_OFFERS_API = 'payments/stripe/subscriptions/upgrade-active';

const isProduction = process.env.APP_STAGE === 'production';

export const HOME_PAGE = '/';
export const ABOUT_PAGE = '/about';
export const OPTION_FLOW_PAGE = '/flow';
export const DARK_POOL_FLOW_PAGE = `${OPTION_FLOW_PAGE}#dark-pool`;
export const HISTORICAL_FLOW_PAGE = '/historical-flow';
export const TICKER_FLOW_PAGE = '/ticker-flow';
export const TOP_TICKERS_PAGE = '/top-tickers';
export const WEEKLY_OVERVIEW_PAGE = '/weekly-overview';
export const ALERTS_PAGE = '/alerts';
export const CRYPTO_PAGE = '/crypto';
export const FOREX_PAGE = '/forex';
export const CHATTER_PAGE = '/chatter';
export const NEWS_PAGE = '/news';
export const getNewsPage = (slug: string) => `${NEWS_PAGE}/${slug}`;
export const getNewsImagePage = (file: string) => `${NEWS_PAGE}/images/${file}`;
export const SOLD_PAGE = '/sold';
export const SIGNUP_PAGE = '/signup';
export const LOGIN_PAGE = '/login';
/**
 * The login address that comes back to `returnTo` once they are in, query string and all. Every
 * signed-out redirect builds its link here so no two of them can send people to different places.
 * `getSafeNextPath` reads the other end of the same contract.
 */
export const getLoginHrefWithNext = (returnTo: string) =>
  returnTo ? `${LOGIN_PAGE}?next=${encodeURIComponent(returnTo)}` : LOGIN_PAGE;
export const FORGOT_PASSWORD_PAGE = '/forgot-password';
export const LOGOUT_PAGE = '/logout';
export const ELECTRONIC_AGREEMENT_PAGE = '/my/electronic-agreement';
export const SETTINGS_PAGE = '/my/settings';
export const ALERTS_SETTINGS_PAGE = '/my/emails';
export const AFFILIATE_DASHBOARD_PAGE = '/my/affiliate';
export const TRADINGVIEW_USERNAME_PAGE = '/my/tradingview';
/** Developer API portal: account, keys and usage for the developer API (cf-workers/api). */
export const DEVELOPER_API_PAGE = '/my/api';
export const DOCS_PAGE = '/docs';
export const API_DOCS_PAGE = '/docs/api';
export const MCP_DOCS_PAGE = '/docs/mcp';
export const PRICING_PAGE = '/pricing';
/** Internal: subscriber growth and the monthly OPRA count. Verified @insiderfinance.io accounts only. */
export const GROWTH_DASHBOARD_PAGE = '/_/admin/growth';
/** Internal: connect brokerage accounts and test each adapter. Verified @insiderfinance.io accounts only. */
export const BROKERAGE_LAB_PAGE = '/_/admin/brokerages';
export const EMAIL_VERIFICATION_PAGE = '/verify-email';
/**
 * Firebase's account email links (verify email, reset password, email changes) open here. Set as
 * the action URL in each Firebase project's email templates; see `screens/Auth/AuthAction`.
 */
export const AUTH_ACTION_PAGE = '/auth/action';
export const TERMS_OF_SERVICE_PAGE = '/terms-of-service';
export const PRIVACY_PAGE = '/privacy';
export const LLMS_TEXT_PAGE = '/llms.txt';
export const LLMS_FULL_TEXT_PAGE = '/llms-full.txt';
export const getEditorialImagePage = (file: string) => `/content/images/${file}`;
export const DISCORD_AUTH_API = '/api/auth/discord';
export const NEWSLETTERS_PAGE = '/newsletter';
export const CHECKOUT_SESSION_SELECTION_API = 'payments/stripe/checkout-session';

export const OPC_PAGE = '/options-profit-calculator';
export const OPC_STRATEGY_PAGE = `${OPC_PAGE}/strategy`;
export const GEX_PAGE = '/gamma-exposure';
export const MARKET_HEATMAP_PAGE = '/heatmaps/sp500';
export const DEX_PAGE = '/delta-exposure';
export const OI_PAGE = '/open-interest-levels';
export const VOL_PAGE = '/implied-volatility';

// Helper functions for GEX/DEX/OI ticker pages
export const getTickerFlowPage = (ticker?: string) => {
  return ticker ? `${TICKER_FLOW_PAGE}/${ticker.toUpperCase()}` : TICKER_FLOW_PAGE;
};

export const getGexPage = (ticker?: string) => {
  return ticker ? `${GEX_PAGE}/${ticker.toUpperCase()}` : GEX_PAGE;
};

export const getDexPage = (ticker?: string) => {
  return ticker ? `${DEX_PAGE}/${ticker.toUpperCase()}` : DEX_PAGE;
};

export const getOiPage = (ticker?: string) => {
  return ticker ? `${OI_PAGE}/${ticker.toUpperCase()}` : OI_PAGE;
};

export const getVolPage = (ticker?: string) => {
  return ticker ? `${VOL_PAGE}/${ticker.toUpperCase()}` : VOL_PAGE;
};

export const CALENDAR_PAGE = `/calendars`;
export const EARNING_CALENDAR_PAGE_PAGE = `${CALENDAR_PAGE}/earnings`;

export const getOpcStrategyPage = (strategyType: string, ticker?: string) => {
  return `${OPC_STRATEGY_PAGE}/${strategyType}${ticker ? `/${ticker}` : ''}`;
};

export const getCalendarPage = (slug: string, ticker?: string) => {
  return `${CALENDAR_PAGE}/${slug}${ticker ? `/${ticker}` : ''}`;
};

export const BLOG_INDEX_PAGE = '/resources';
export const RESOURCES_INDEX_PAGE = BLOG_INDEX_PAGE;
export const getBlogPostPage = (slug: string) => `${BLOG_INDEX_PAGE}/${slug}`;

export const NEWS_INDEX_PAGE = '/news';
export const getNewsPostPage = (slug: string) => `${NEWS_INDEX_PAGE}/${slug}`;

export const FINANCE_GLOSSARY_INDEX_PAGE = '/finance-glossary';
export const getFinanceGlossaryTermPage = (slug: string) =>
  `${FINANCE_GLOSSARY_INDEX_PAGE}/${slug}`;

export const COURSES_INDEX_PAGE = '/learn';
// In-app education hub (dashboard chrome). Holds both the courses and resource center via `?tab=`.
export const EDUCATION_PAGE = '/education';
export const EDUCATION_RESOURCES_PAGE = `${EDUCATION_PAGE}?tab=resources`;
export const getMasterClassPage = (slug: string) => `${COURSES_INDEX_PAGE}/course/${slug}`;
export const getMasterClassLessonPage = (courseSlug: string, lessonSlug: string) =>
  `${COURSES_INDEX_PAGE}/course/${courseSlug}/lesson/${lessonSlug}`;

export const AFFILIATE_PROGRAM_ARTICLE_PAGE =
  '/resources/earn-monthly-income-with-the-insiderfinance-affiliate-program';
export const MASTER_CLASS_ARTICLE_PAGE = COURSES_INDEX_PAGE;

export const DASHBOARD_SECTION = 'dashboardOverview';
export const PRICING_SECTION = 'pricingCards';
export const TECHNICAL_ANALYSIS_LANDING_PAGE = '/technical-analysis';
export const EXCLUSIVE_EVENT_LANDING_PAGE = '/exclusive-event';

export const CONGRESS_TRADES_PAGE = '/congress-trades';
export const CONGRESS_PERSON_PAGE_BASE = '/congress-trades/politician';

export const INSIDER_TRADES_PAGE = '/insider-trades';

export const DISCORD_BOT_DOCS_PAGE = '/docs/discord-bot';

export const getCongressPersonPage = (slug: string) => {
  return `${CONGRESS_PERSON_PAGE_BASE}/${slug}`;
};
export const NANCY_PELOSI_PAGE = getCongressPersonPage('nancy-pelosi');

export const getInsiderTradesPage = (ticker?: string) => {
  return `${OPC_STRATEGY_PAGE}/${ticker ? `/${ticker}` : ''}`;
};

export const MONTHLY_TIER = 'monthly';
export const QUARTERLY_TIER = 'quarterly';
export const YEARLY_TIER = 'annual';
export const DASHBOARD_REGISTER = 'dashboard';
export const DASHBOARD_SIGNUP_MONTHLY = `${SIGNUP_PAGE}/${DASHBOARD_REGISTER}/${MONTHLY_TIER}`;
export const DASHBOARD_SIGNUP_QUARTERLY = `${SIGNUP_PAGE}/${DASHBOARD_REGISTER}/${QUARTERLY_TIER}`;
export const DASHBOARD_SIGNUP_YEARLY = `${SIGNUP_PAGE}/${DASHBOARD_REGISTER}/${YEARLY_TIER}`;
export const TECHNICAL_ANALYSIS_SIGNUP_MONTHLY = `${SIGNUP_PAGE}/${
  isProduction ? ProductionAlertPrices.monthly : DevAlertPrices.monthly
}`;
export const TECHNICAL_ANALYSIS_SIGNUP_QUARTERLY = `${SIGNUP_PAGE}/${
  isProduction ? ProductionAlertPrices.quarterly : DevAlertPrices.quarterly
}`;
export const TECHNICAL_ANALYSIS_SIGNUP_YEARLY = `${SIGNUP_PAGE}/${
  isProduction ? ProductionAlertPrices.annual : DevAlertPrices.annual
}`;
export const DASHBOARD_LOGIN_MONTHLY = `${LOGIN_PAGE}/${DASHBOARD_REGISTER}/${MONTHLY_TIER}`;
export const DASHBOARD_LOGIN_QUARTERLY = `${LOGIN_PAGE}/${DASHBOARD_REGISTER}/${QUARTERLY_TIER}`;
export const DASHBOARD_LOGIN_YEARLY = `${LOGIN_PAGE}/${DASHBOARD_REGISTER}/${YEARLY_TIER}`;
export const TECHNICAL_ANALYSIS_LOGIN_MONTHLY = `${LOGIN_PAGE}/${
  isProduction ? ProductionAlertPrices.monthly : DevAlertPrices.monthly
}`;
export const TECHNICAL_ANALYSIS_LOGIN_QUARTERLY = `${LOGIN_PAGE}/${
  isProduction ? ProductionAlertPrices.quarterly : DevAlertPrices.quarterly
}`;
export const TECHNICAL_ANALYSIS_LOGIN_YEARLY = `${LOGIN_PAGE}/${
  isProduction ? ProductionAlertPrices.annual : DevAlertPrices.annual
}`;

// -- Exclusive event
export const EXCLUSIVE_EVENT_SIGNUP_MONTHLY = `${SIGNUP_PAGE}/${
  isProduction ? ProductionExclusiveEventPrices.monthly : DevExclusiveEventPrices.monthly
}`;
export const EXCLUSIVE_EVENT_SIGNUP_QUARTERLY = `${SIGNUP_PAGE}/${
  isProduction ? ProductionExclusiveEventPrices.quarterly : DevExclusiveEventPrices.quarterly
}`;
export const EXCLUSIVE_EVENT_SIGNUP_YEARLY = `${SIGNUP_PAGE}/${
  isProduction ? ProductionExclusiveEventPrices.annual : DevExclusiveEventPrices.annual
}`;
export const EXCLUSIVE_EVENT_LOGIN_MONTHLY = `${LOGIN_PAGE}/${
  isProduction ? ProductionExclusiveEventPrices.monthly : DevExclusiveEventPrices.monthly
}`;
export const EXCLUSIVE_EVENT_LOGIN_QUARTERLY = `${LOGIN_PAGE}/${
  isProduction ? ProductionExclusiveEventPrices.quarterly : DevExclusiveEventPrices.quarterly
}`;
export const EXCLUSIVE_EVENT_LOGIN_YEARLY = `${LOGIN_PAGE}/${
  isProduction ? ProductionExclusiveEventPrices.annual : DevExclusiveEventPrices.annual
}`;

export const authPages = [FORGOT_PASSWORD_PAGE];

export const privatePages = [
  // OPTION_FLOW_PAGE,
  // HISTORICAL_FLOW_PAGE,
  // TICKER_FLOW_PAGE,
  // TOP_TICKERS_PAGE,
  // WEEKLY_OVERVIEW_PAGE,
  // CRYPTO_PAGE,
  // FOREX_PAGE,
  // NEWS_PAGE,
  // ELECTRONIC_AGREEMENT_PAGE,
  SETTINGS_PAGE,
  // ALERTS_SETTINGS_PAGE,
];

export const PageIcons = {
  OptionFlow: RiPulseLine,
  Opc: BiCalculator,
  TickerResearch: VscBeaker,
  Gex: TbBrandZapier,
  TopTickers: AiOutlineFire,
  WeeklyOverview: BiCalendarWeek,
  HistoricalFlow: MdAccessTime,
  Crypto: BiBitcoin,
  Forex: BiDollar,
  News: BsNewspaper,
  Masterclass: HiOutlineAcademicCap,
  Resources: HiOutlineBookOpen,
  Sold: BiArrowToBottom,
  CongressTrades: PiGavel,
  InsiderTrades: GiLouvrePyramid,
  Calendar: SidebarCalendarIcon,
};

export interface SidebarItem {
  href: string;
  Icon: (typeof PageIcons)[keyof typeof PageIcons];
  name: string;
  /** Extra Next.js router.pathname values that should highlight this sidebar item. */
  activePathnames?: string[];
  /** router.pathname prefixes that should highlight this sidebar item (e.g. for arbitrary dynamic subtrees). */
  activePathPrefixes?: string[];
}

export const SIDEBAR_TOP_ITEMS: SidebarItem[] = [
  {
    href: OPTION_FLOW_PAGE,
    Icon: PageIcons.OptionFlow,
    name: 'Flow',
    activePathnames: [OPTION_FLOW_PAGE, HISTORICAL_FLOW_PAGE, WEEKLY_OVERVIEW_PAGE],
  },
  {
    href: OPC_PAGE,
    Icon: PageIcons.Opc,
    name: 'Options Profit Calculator',
    activePathPrefixes: [`${OPC_STRATEGY_PAGE}/`],
  },
  {
    href: GEX_PAGE,
    Icon: PageIcons.Gex,
    name: 'Gamma Exposure',
    activePathnames: [
      `${GEX_PAGE}/[ticker]`,
      DEX_PAGE,
      `${DEX_PAGE}/[ticker]`,
      OI_PAGE,
      `${OI_PAGE}/[ticker]`,
      VOL_PAGE,
      `${VOL_PAGE}/[ticker]`,
    ],
  },
  {
    href: TICKER_FLOW_PAGE,
    Icon: PageIcons.TickerResearch,
    name: 'Ticker Research',
    activePathnames: [`${TICKER_FLOW_PAGE}/[ticker]`],
  },
  // !getIsNativePlatform()
  //   ? {
  //       href: CRYPTO_PAGE,
  //       Icon: PageIcons.Crypto,
  //       name: 'Cryptocurrency',
  //     }
  //   : null,
  // !getIsNativePlatform()
  //   ? {
  //       href: FOREX_PAGE,
  //       Icon: PageIcons.Forex,
  //       name: 'Forex',
  //     }
  //   : null,
  {
    href: EARNING_CALENDAR_PAGE_PAGE,
    Icon: PageIcons.Calendar,
    name: 'Earnings Calendar',
    // `/calendars/earnings` is served by `pages/calendars/[slug].tsx`, so router.pathname is the dynamic form.
    activePathnames: [`${CALENDAR_PAGE}/[slug]`, `${CALENDAR_PAGE}/[slug]/[symbol]`],
  },
  {
    href: TOP_TICKERS_PAGE,
    Icon: PageIcons.TopTickers,
    name: 'Top Tickers',
  },
  {
    href: NEWS_PAGE,
    Icon: PageIcons.News,
    name: 'News with Sentiment',
    activePathnames: [`${NEWS_PAGE}/[slug]`],
  },
  {
    href: INSIDER_TRADES_PAGE,
    Icon: PageIcons.InsiderTrades,
    name: 'Insider Trades',
    activePathnames: [
      `${INSIDER_TRADES_PAGE}/ticker/[ticker]`,
      `${INSIDER_TRADES_PAGE}/person/[person]`,
    ],
  },
  {
    href: CONGRESS_TRADES_PAGE,
    Icon: PageIcons.CongressTrades,
    name: 'Congress Trades',
    activePathPrefixes: [`${CONGRESS_PERSON_PAGE_BASE}/`],
  },
];

export const SIDEBAR_EDUCATION_ITEM: SidebarItem = {
  href: EDUCATION_PAGE,
  Icon: PageIcons.Masterclass,
  name: 'Education',
  // The resource center lives on the same route via `?tab=resources`, so router.pathname stays `/education`.
  activePathPrefixes: [`${EDUCATION_PAGE}/`],
};

export function isSidebarItemActive(item: SidebarItem, pathname?: string): boolean {
  if (!pathname) return false;
  if (pathname === item.href) return true;
  if (item.activePathnames?.includes(pathname)) return true;
  return !!item.activePathPrefixes?.some((prefix) => pathname.startsWith(prefix));
}

export const SIDEBAR_BOTTOM_ITEMS = [
  // {
  //   href: LOGOUT_PAGE,
  //   Icon: FiLogOut,
  //   name: 'Logout',
  // },
  {
    href: SETTINGS_PAGE,
    Icon: FaUser,
    name: 'Settings',
  },
];

// API ROUTES
export const UPGRADE_SUBSCRIPTION_API = 'payments/stripe/subscriptions/upgrade-newest';
export const NEWSLETTER_SHARE_API = '/share/newsletter';

export const EMAIL_OFFERS_API = 'payments/stripe/subscriptions/email-offers';
